import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import {
  getProductCatalog,
  getMedias,
  getProductTypes,
  getInheritedVariableCosts,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { useExtensions } from '../../../app/ExtensionsContext';
import { usePageTitle } from '../../../layout/PageTitleContext';
import { formatCurrency } from '../../../app/currency';
import {
  PRODUCT_DEFAULT_IMAGE,
  ReadOnlyMediaCarousel,
  filterMediasByMetadata,
  mediasToCarouselItems,
} from '../../medias/public';
import { PriceSimulatorSavedSummary } from '../../../extensions/price-simulator-3d/PriceWithSimulatorField';
import PriceSimulatorTriggerButton from '../../../extensions/price-simulator-3d/PriceSimulatorTriggerButton';
import {
  getProductVariableCostDefaults,
  getResolvedSavedSimulation,
  PRICE_SIMULATOR_EXTENSION_KEY,
  resolveCostForSimulationApply,
  resolveEffectiveVariableCostDefaults,
} from '../../../extensions/price-simulator-3d/priceSimulatorMetadata';
import ProductDescriptionContent from '../private/ProductDescriptionContent';
import {
  normalizeCatalog,
  normalizeProductType,
  buildVariantTitle,
  isVariantReadOnly,
} from '../private/productVariantUtils';

function productSlug(catalog) {
  return catalog?.code || catalog?.id || '';
}

function itemTypeLabel(type, translate) {
  const key = String(type || 'PRODUCT').toUpperCase();
  if (key === 'SERVICE') return translate('products.item_type_service');
  if (key === 'SUPPLY') return translate('products.item_type_supply');
  return translate('products.item_type_product');
}

function VariantViewCard({
  variant,
  options,
  productCarouselItems,
  productVariableCostDefaults = [],
  simulatorEnabled,
  translate,
}) {
  const savedSimulation = getResolvedSavedSimulation(variant.metadata, productVariableCostDefaults);
  const displayCost = savedSimulation
    ? resolveCostForSimulationApply(savedSimulation, variant.cost ?? 0)
    : variant.cost ?? 0;
  const displayPrice = savedSimulation?.result?.pricing?.final_price ?? variant.price ?? 0;
  const simulationQuantity = Math.max(
    1,
    parseInt(savedSimulation?.input?.product?.quantity, 10) ||
      parseInt(savedSimulation?.result?.detail?.quantity, 10) ||
      1,
  );
  const printHours = Number(savedSimulation?.input?.product?.print_hours ?? 0);

  const title = buildVariantTitle(variant, options);
  const skuCarouselItems = variant.skuCarouselItems?.length
    ? variant.skuCarouselItems
    : productCarouselItems;

  const stockValue = variant.is_made_to_order
    ? String(variant.manufacturing_days || 0)
    : `${variant.stock ?? 0}${variant.stock_unit ? ` ${variant.stock_unit}` : ''}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ReadOnlyMediaCarousel
        items={skuCarouselItems}
        defaultImageUrl={PRODUCT_DEFAULT_IMAGE}
        compact
      />
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} title={title} sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          {variant.code ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {variant.code}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
              {translate('products.list_cost')}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(displayCost)}
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {translate('products.price')}
              </Typography>
              {simulatorEnabled && savedSimulation ? (
                <PriceSimulatorTriggerButton
                  savedSimulation={savedSimulation}
                  showTrigger={false}
                />
              ) : null}
            </Box>
            <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(displayPrice)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
              {variant.is_made_to_order
                ? translate('products.manufacturing_days')
                : translate('products.stock')}
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {stockValue}
            </Typography>
          </Box>
        </Box>

        {simulatorEnabled && savedSimulation?.result ? (
          <Box sx={{ mt: 1, flexShrink: 0, width: '100%' }}>
            {printHours > 0 ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {translate('price_simulate.print_hours')}: {printHours}
              </Typography>
            ) : null}
            <PriceSimulatorSavedSummary
              savedSimulation={savedSimulation}
              quantity={simulationQuantity}
              compact
              stacked
            />
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
}

const ProductViewPage = () => {
  const { id } = useParams();
  const { translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isEnabled } = useExtensions();
  const { setPageTitle } = usePageTitle();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [productCarouselItems, setProductCarouselItems] = useState([]);
  const [variantMediaMap, setVariantMediaMap] = useState({});
  const [typeNameById, setTypeNameById] = useState({});
  const [inheritedVariableCosts, setInheritedVariableCosts] = useState([]);

  const simulatorEnabled = isEnabled(PRICE_SIMULATOR_EXTENSION_KEY);

  const mainCategoryId = useMemo(() => {
    const sorted = [...(catalog?.categories || [])].sort((a, b) => a.display_order - b.display_order);
    const main = sorted.find((c) => c.is_main) || sorted[0];
    return main?.category_id || '';
  }, [catalog?.categories]);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [catalogRes, mediasRes, typesRes] = await Promise.all([
        getProductCatalog(id),
        getMedias(),
        getProductTypes().catch(() => ({ data: [] })),
      ]);

      const normalized = normalizeCatalog(catalogRes.data);
      if (!normalized?.id) {
        setLoadError(translate('products.view_not_found'));
        setCatalog(null);
        return;
      }

      const allMedias = mediasRes?.data?.medias || mediasRes?.data || [];
      const productMedias = filterMediasByMetadata(allMedias, 'product_image', normalized.id);
      const productItems = mediasToCarouselItems(productMedias);

      const vMediaMap = {};
      (normalized.variants || []).forEach((variant) => {
        const skuMedias = filterMediasByMetadata(allMedias, 'sku_image', variant.id);
        vMediaMap[variant.id] = mediasToCarouselItems(skuMedias);
      });

      const typesList = Array.isArray(typesRes?.data) ? typesRes.data : [];
      const nameMap = {};
      typesList.forEach((row) => {
        const t = normalizeProductType(row);
        if (t?.id) nameMap[t.id] = t.name;
      });

      setCatalog(normalized);
      setProductCarouselItems(productItems);
      setVariantMediaMap(vMediaMap);
      setTypeNameById(nameMap);
      setPageTitle((normalized.name || '').trim() || translate('products.view'));
    } catch {
      setLoadError(translate('products.view_load_error'));
      setCatalog(null);
    } finally {
      setLoading(false);
    }
  }, [id, translate, setPageTitle]);

  useEffect(() => {
    void loadData();
    return () => setPageTitle(null);
  }, [loadData, setPageTitle]);

  useEffect(() => {
    if (!simulatorEnabled || !mainCategoryId) {
      setInheritedVariableCosts([]);
      return;
    }
    let cancelled = false;
    void getInheritedVariableCosts(mainCategoryId)
      .then((res) => {
        if (!cancelled) {
          setInheritedVariableCosts(res.data?.defaults ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) setInheritedVariableCosts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [simulatorEnabled, mainCategoryId]);

  const activeVariants = useMemo(() => {
    if (!catalog) return [];
    return (catalog.variants || [])
      .filter(
        (v) =>
          (v.status || 'ACTIVE') === 'ACTIVE' && !isVariantReadOnly(v, catalog.options),
      )
      .map((v) => ({
        ...v,
        skuCarouselItems: variantMediaMap[v.id] || [],
      }));
  }, [catalog, variantMediaMap]);

  const typeChips = useMemo(() => {
    if (!catalog?.type_ids?.length) return [];
    return catalog.type_ids
      .map((typeId) => typeNameById[typeId])
      .filter(Boolean);
  }, [catalog, typeNameById]);

  const editPath = catalog ? `/products/${encodeURIComponent(productSlug(catalog))}/edit` : '/products';
  const displayName = (catalog?.name || '').trim() || translate('products.unnamed_label');
  const productVariableCostDefaults = useMemo(
    () => resolveEffectiveVariableCostDefaults({
      inheritedCategoryRows: inheritedVariableCosts,
      productRows: getProductVariableCostDefaults(catalog?.metadata || {}),
    }),
    [inheritedVariableCosts, catalog?.metadata],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !catalog) {
    return (
      <Box>
        <Typography color="error" sx={{ mb: 2 }}>
          {loadError || translate('products.view_not_found')}
        </Typography>
        <Button component={RouterLink} to="/products" variant="outlined">
          {translate('products.title')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2,
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          <Link component={RouterLink} to="/products" underline="hover" color="inherit">
            {translate('products.title')}
          </Link>
          <Typography color="text.primary" noWrap sx={{ maxWidth: isMobile ? 180 : 400 }}>
            {displayName}
          </Typography>
        </Breadcrumbs>
        <Button
          component={RouterLink}
          to={editPath}
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
        >
          {translate('products.view_edit')}
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <ReadOnlyMediaCarousel
              items={productCarouselItems}
              defaultImageUrl={PRODUCT_DEFAULT_IMAGE}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
              {displayName}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Chip
                label={
                  catalog.status === 'ACTIVE'
                    ? translate('products.active')
                    : translate('products.inactive')
                }
                size="small"
                color={catalog.status === 'ACTIVE' ? 'success' : 'default'}
              />
              <Chip label={itemTypeLabel(catalog.type, translate)} size="small" variant="outlined" />
              {typeChips.map((name) => (
                <Chip key={name} label={name} size="small" variant="outlined" />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {translate('products.code')}: {catalog.code || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              {translate('products.item_type')}: {itemTypeLabel(catalog.type, translate)}
            </Typography>
            <Divider sx={{ mb: 2, display: { xs: 'none', md: 'block' } }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {translate('products.description')}
            </Typography>
            <ProductDescriptionContent description={catalog.description} />
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        {translate('products.view_variants_section')}
        {activeVariants.length > 0 ? ` (${activeVariants.length})` : ''}
      </Typography>

      {activeVariants.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">{translate('products.view_no_active_variants')}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {activeVariants.map((variant) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={variant.id} sx={{ display: 'flex' }}>
              <VariantViewCard
                variant={variant}
                options={catalog.options}
                productCarouselItems={productCarouselItems}
                productVariableCostDefaults={productVariableCostDefaults}
                simulatorEnabled={simulatorEnabled}
                translate={translate}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ProductViewPage;
